import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
  LayoutDashboard,
  Building2,
  Users,
  Receipt,
  Gift,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  UserCircle,
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/properties', label: 'Properties', icon: Building2 },
  { path: '/agents', label: 'Agents', icon: Users },
  { path: '/sales', label: 'Sales Records', icon: Receipt },
  { path: '/customers', label: 'Customers', icon: UserCircle },
  { path: '/bonus-tracker', label: 'Bonus Tracker', icon: Gift, mdOnly: true },
  { path: '/invoices', label: 'Invoice', icon: FileText },
  { path: '/settings', label: 'Settings', icon: Settings, mdOnly: true },
];

export const Sidebar = () => {
  const { user, logout, isMD } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => !item.mdOnly || isMD);

  // Extract company name for sidebar display
  const companyName = settings?.company_name || 'Decedas Global Resources Limited';
  const nameParts = companyName.split(' ');
  const displayName = nameParts[0]?.toUpperCase() || 'DECEDAS';
  const brandLogo = settings?.company_logo || '/logo.png';

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-lux-border">
        <div className="mb-4 flex justify-center">
          <img
            src={brandLogo}
            alt="Company Logo"
            className="h-14 w-auto max-w-[180px] object-contain"
            data-testid="sidebar-company-logo"
          />
        </div>
        <h1 className="font-heading text-2xl text-lux-gold font-bold tracking-tight">
          {displayName}
        </h1>
        <p className="text-xs text-lux-muted mt-1 tracking-widest uppercase line-clamp-2">
          {nameParts.slice(1).join(' ') || 'Global Resources'}
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User info and logout */}
      <div className="p-4 border-t border-lux-border">
        <div className="mb-3 px-4">
          <p className="text-sm font-medium text-lux-text truncate">{user?.full_name}</p>
          <p className="text-xs text-lux-muted capitalize">{user?.role === 'md' ? 'Managing Director' : 'Agent'}</p>
        </div>
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-lux-card/95 backdrop-blur-xl border-r border-lux-border z-40 no-print">
        <NavContent />
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-lux-card/95 backdrop-blur-xl border-b border-lux-border z-40 flex items-center justify-between px-4 no-print">
        <div className="flex items-center gap-2">
          <img
            src={brandLogo}
            alt="Company Logo"
            className="h-8 w-auto max-w-[120px] object-contain"
            data-testid="mobile-header-company-logo"
          />
          <h1 className="font-heading text-xl text-lux-gold font-bold">{displayName}</h1>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-lux-muted hover:text-lux-text"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40 no-print"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-lux-card/95 backdrop-blur-xl border-r border-lux-border z-50 transform transition-transform duration-300 no-print ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <NavContent />
      </aside>

      {/* Mobile Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-lux-card/95 backdrop-blur-xl border-t border-lux-border z-30 flex items-center justify-around px-2 no-print">
        {filteredNavItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                isActive ? 'text-lux-gold' : 'text-lux-muted hover:text-lux-text'
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] mt-1">{item.label.split(' ')[0]}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-lux-black">
      <Sidebar />
      <main className="lg:pl-64 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};
